import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  HomeIcon,
  CameraIcon,
  LightBulbIcon,
  BeakerIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  CpuChipIcon,
  DocumentTextIcon,
  BookOpenIcon,
  QrCodeIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  ArrowLeftOnRectangleIcon,
  CircleStackIcon,
  CalendarDaysIcon,
  ArchiveBoxIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ReceiptPercentIcon,
  ArrowsRightLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

const Sidebar = ({ mobileMenuOpen = false, onCloseMobileMenu = () => {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isAdmin } = useAuth();
  const [financialExpanded, setFinancialExpanded] = useState(
    location.pathname.startsWith('/financial') || location.pathname === '/analytics'
  );

  // Farm Details section
  const farmDetailsItems = [
    { path: '/dashboard', name: 'Dashboard', icon: HomeIcon },
    { path: '/utilisation', name: 'Utilisation', icon: CpuChipIcon },
    { path: '/reports', name: 'Report', icon: DocumentTextIcon },
  ];

  // Financial sub-items
  const financialSubItems = [
    { path: '/financial/sales', name: 'Sales', icon: CurrencyDollarIcon },
    { path: '/financial/cost', name: 'Cost', icon: ReceiptPercentIcon },
    { path: '/financial/analytics', name: 'Analytics', icon: ChartBarIcon },
    { path: '/financial/transaction', name: 'Transaction', icon: ArrowsRightLeftIcon },
  ];

  // Farm Management section
  const farmManagementItems = [
    { path: '/farm-management', name: 'Farm Management', icon: BuildingOfficeIcon },
    { path: '/planting-schedule', name: 'Planting Schedule', icon: CalendarDaysIcon },
    { path: '/harvest', name: 'Harvest', icon: ArchiveBoxIcon },
    { path: '/attendance', name: 'Attendance', icon: ClipboardDocumentCheckIcon },
    { path: '/itraceability', name: 'iTraceability', icon: QrCodeIcon },
  ];

  // Help section
  const helpItems = [
    { path: '/documentation', name: 'Documentation', icon: BookOpenIcon },
  ];

  // IoT Partners section
  const iotPartnersItems = [
    { path: '/havva', name: 'HAVVA', icon: CameraIcon },
    { path: '/avialite', name: 'Avialite', icon: LightBulbIcon },
    { path: '/elliance', name: 'Elliance', icon: BeakerIcon },
  ];

  const adminMenuItems = [
    { path: '/database-management', name: 'Database Management', icon: CircleStackIcon },
    { path: '/organization-management', name: 'Organization Management', icon: BuildingStorefrontIcon }
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div
      className={`
        w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0
        fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 ease-out
        md:static md:translate-x-0
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}
    >
      {/* Logo + mobile close */}
      <div className="px-6 py-4 border-b border-gray-200 h-[69px] flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <img src="/logo-Kapitani.png" alt="Kapitani Logo" className="w-8 h-8 object-contain" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900">Kapitani</div>
          </div>
        </div>
        <button
          onClick={onCloseMobileMenu}
          className="md:hidden text-gray-400 hover:text-gray-700"
          aria-label="Close menu"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {/* Farm Details Section */}
        <div className="pb-2">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 mb-2">
            Farm Details
          </div>
          {farmDetailsItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.name}
              </NavLink>
            );
          })}
          
          {/* Financial Expandable Menu */}
          <div>
            <button
              onClick={() => setFinancialExpanded(!financialExpanded)}
              className={`sidebar-item w-full justify-between ${
                location.pathname.startsWith('/financial') ? 'active' : ''
              }`}
            >
              <div className="flex items-center">
                <BanknotesIcon className="h-5 w-5 mr-3" />
                Financial
              </div>
              {financialExpanded ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
            {financialExpanded && (
              <div className="ml-4 mt-1 space-y-1">
                {financialSubItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={`sidebar-item text-sm ${isActive ? 'active' : ''}`}
                    >
                      <Icon className="h-4 w-4 mr-3" />
                      {item.name}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Farm Management Section */}
        <div className="py-2">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 mb-2">
            Farm Management
          </div>
          {farmManagementItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.name}
              </NavLink>
            );
          })}
        </div>

        {/* IoT Partners Section */}
        <div className="py-2">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 mb-2">
            IoT Partners
          </div>
          {iotPartnersItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.name}
              </NavLink>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="py-2">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 mb-2">
            Help
          </div>
          {helpItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.name}
              </NavLink>
            );
          })}
        </div>

        {/* Admin Section - Only visible to admins */}
        {isAdmin && (
          <div className="py-2">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 mb-2">
              Admin
            </div>
            {adminMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </NavLink>
              );
            })}
          </div>
        )}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-gray-200">
        <button 
          onClick={handleLogout}
          className="sidebar-item w-full text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-3" />
          Log Out
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
